import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import {
  RdsButtonModule,
  RdsProgressSpinnerModule,
} from '@rds/angular-components';

import { HeaderComponent } from './components/panorama/header/header.component';
import { FooterComponent } from './components/panorama/footer/footer.component';
import { AuthService } from './services/auth.service';
import {
  FOCUS_HEADER,
  PANORAMA_HEADER,
  SHARED_FOOTER,
} from './constants/layout.constants';

/**
 * Application shell: header, routed content, footer.
 *
 * This used to fetch a Storyblok story and render the whole layout — including
 * the `<router-outlet>` — through `ndc-dynamic`. That put a CMS round-trip in
 * front of every first paint for a payload whose entire content was the header
 * and footer configuration now held in `layout.constants`.
 *
 * The shell is withheld until `authGuard` has ruled. Every route in the app is
 * guarded, so painting header and footer before that only ever produced a
 * second or two of chrome around an empty page that was about to be replaced by
 * Ping — see `AuthService.canRenderShell`.
 */
@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    RdsProgressSpinnerModule,
    RdsButtonModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _auth = inject(AuthService);
  private _sub?: Subscription;

  /** True only once the user is known to be allowed in. */
  readonly canRenderShell = computed(
    () => this._auth.phase() === 'authenticated',
  );

  /** Distinguishes "still deciding" from "we gave up", which read differently. */
  readonly hasLoginFailed = computed(() => this._auth.phase() === 'failed');

  /** Says what is actually happening rather than promising content that is not coming. */
  readonly signInMessage = computed(() =>
    this._auth.phase() === 'redirecting'
      ? 'Taking you to Roche sign-in…'
      : 'Checking your Roche session…',
  );

  /** Only present while the failure screen is up, which is what makes the effect below fire. */
  private readonly _failureHeading =
    viewChild<ElementRef<HTMLHeadingElement>>('failureHeading');

  constructor() {
    /* The failure screen replaces the whole document, so a live region inside
     * it is inserted already populated and may not announce. Moving focus to
     * the heading is the reliable signal: it reads the heading and leaves the
     * user one Tab from "Try again". */
    effect(() => this._failureHeading()?.nativeElement.focus());
  }

  private readonly _mode = signal<'focus' | 'panorama'>('panorama');

  /** Only the secondary navigation differs between the two modes. */
  readonly header = computed(() =>
    this._mode() === 'focus' ? FOCUS_HEADER : PANORAMA_HEADER,
  );
  readonly footer = SHARED_FOOTER;
  readonly isFocus = computed(() => this._mode() === 'focus');

  ngOnInit(): void {
    this._sub = this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map(() => this._router.url),
        // Seed with the current URL so the initial route is handled even if its
        // NavigationEnd fired before this subscription registered (bootstrap race).
        startWith(this._router.url),
        map((url) => (url.startsWith('/focus') ? 'focus' : 'panorama')),
        distinctUntilChanged(),
      )
      .subscribe((mode) => this._mode.set(mode));
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  retryLogin(): void {
    this._auth.retryLogin();
  }
}
